import z from "zod";

export const AuthUserInfoSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  phone: z.string(),
  enabled: z.boolean(),
  roles: z.array(z.string()),
  username: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email_verified: z.boolean().default(false),
  phone_verified: z.boolean().default(false),
  onboarding_complete: z.boolean().default(false),
});

export const AuthResponseSchema = z.object({
  message: z.string().nullable(),
  success: z.boolean(),
  accessToken: z.string(),
  refreshToken: z.string(),
  user_info: AuthUserInfoSchema,
});

export const RefreshResponseSchema = z.object({
  success: z.boolean(),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  message: z.string().optional(),
});

export type AuthUserInfo = z.infer<typeof AuthUserInfoSchema>;

export type UserInfo = z.infer<typeof AuthUserInfoSchema> & {
  onboarding_complete: boolean;
};

export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// Note: User-facing validation messages live in the components that use
// these schemas (e.g. signup.tsx rebuilds SignupSchema via useMemo so error
// messages come from i18n). The bare schema here exists for the TypeScript
// type only (SignupData) and as a safe fallback — the global zodErrorMap
// handles generic messages for bare validators.
export const SignupSchema = z
  .object({
    firstname: z
      .string()
      .min(1)
      .refine((val) => val.trim().length > 0),
    lastname: z
      .string()
      .min(1)
      .refine((val) => val.trim().length > 0),
    email: z.string().email(),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
    role: z.union([z.literal("worker"), z.literal("employer")]),
    password: z
      .string()
      .min(8)
      .regex(/[A-Z]/)
      .regex(/[a-z]/)
      .regex(/[0-9]/)
      .regex(/[@$!%*?&]/)
      .regex(/^[A-Za-z0-9@$!%*?&]+$/),
    password2: z.string(),
    // Optional here; signup.tsx makes it required when VITE_REQUIRE_INVITE_CODE is on.
    inviteCode: z.string().optional(),
    // Token from a restaurant's worker-invite link; stands in for the invite code.
    workerInviteToken: z.string().optional(),
  })
  .refine((data) => data.password === data.password2, {
    path: ["password2"],
  });

export type SignupData = z.infer<typeof SignupSchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

export type LoginData = z.infer<typeof LoginSchema>;

export const ResendVerificationSchema = z.object({
  type: z.enum(["email", "phone"]),
  code: z.string().optional(),
});

export type ResendVerificationData = z.infer<typeof ResendVerificationSchema>;
